import { Box, Button, Chip, Skeleton, Typography } from '@mui/material';
import { type ReactElement } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';

import ErrorState from 'app/shared-components/common/ErrorState';

import HeaderAndFooterTab from './HeaderAndFooter/HeaderAndFooterTab';
import { useGetPages } from './hooks/useGetPages';
import type { Page } from './hooks/useGetPages';
import usePlusWebsites, { resolveLandingType } from '../../hooks/usePlusWebsites';
import { HomePageCard } from './components/HomePageCard';
import { BespokePlanCard } from './components/BespokePlanCard';
import { PageActionsMenu } from './components/PageActionsMenu';
import type { PageLandingSwitch } from './components/PageActionsMenu';
import { AddPageRow } from './components/AddPageRow';
import { HOME_PAGE, IS_STANDARD_PAGE_HREF, STANDARD_PAGES } from './standardPages';
import { useIsBespoke } from '../../hooks/useWebBuilderAccess';

const SUB_TABS = [
  { label: 'Pages', key: 'pages' },
  { label: 'Header & Footer', key: 'header-footer' },
  { label: 'Blog - Coming soon', key: null },
  { label: 'Site map - Coming soon', key: null },
];

function getChipSx(isSelected: boolean, isEnabled: boolean) {
  if (isSelected) return { borderColor: 'black', fontWeight: 500, padding: '12px' };
  if (isEnabled) return { padding: '12px' };
  return { borderColor: '#d1d5db', color: '#9ca3af', padding: '12px' };
}

const ContentTab = (): ReactElement => {
  const { websiteId } = useOutletContext<{ websiteId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('contentTab') ?? 'pages';

  const handleTabChange = (key: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('contentTab', key);
      return next;
    });
  };

  const activeContent =
    activeTab === 'header-footer' ? (
      <HeaderAndFooterTab websiteId={websiteId} />
    ) : (
      <PagesTabContent websiteId={websiteId} />
    );

  return (
    <Box>
      <Box className="vtwo:flex vtwo:gap-8 vtwo:px-16 vtwo:py-8">
        {SUB_TABS.map((tab) => {
          const isEnabled = tab.key !== null;
          const isSelected = isEnabled && tab.key === activeTab;
          return (
            <Chip
              key={tab.label}
              label={tab.label}
              variant="outlined"
              size="small"
              clickable={isEnabled}
              onClick={isEnabled ? () => handleTabChange(tab.key!) : undefined}
              sx={getChipSx(isSelected, isEnabled)}
            />
          );
        })}
      </Box>
      {activeContent}
    </Box>
  );
};

function PagesTabContent({ websiteId }: { websiteId: string }): ReactElement {
  const {
    pages,
    isLoading: isLoadingPages,
    isError: isPagesError,
    error,
  } = useGetPages({
    websiteId,
  });
  const {
    plusWebsites,
    isLoading: isLoadingWebsites,
    isError: isWebsitesError,
  } = usePlusWebsites();
  const { isBespoke, isLoading: isLoadingBespoke } = useIsBespoke();
  const website = plusWebsites.find((candidate) => candidate.id === websiteId);
  const isLoading = isLoadingPages || isLoadingWebsites;
  const landingType = resolveLandingType(website);
  const pageByHref: Record<string, Page | undefined> = Object.fromEntries(
    pages.map((page) => [page.href, page])
  );
  const hasHomePage = Boolean(pageByHref[HOME_PAGE.href]);
  const customPages = pages.filter((page) => !IS_STANDARD_PAGE_HREF[page.href]);
  const isExternal = landingType === 'external';

  if (isPagesError || isWebsitesError) {
    return (
      <ErrorState
        title="Failed to load pages"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  // Landing behaviour drives the whole tab, so the controls stay hidden until the
  // website record is known — otherwise an external website would briefly be
  // treated as in-app.
  if (!isLoading && !website) {
    return <ErrorState title="Website not found" subtitle="This website is no longer available" />;
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-3 vtwo:px-16 vtwo:pb-16">
      {!isLoading && !isLoadingBespoke && isBespoke && <BespokePlanCard domain={website?.domain} />}

      {/* The card stands in for the home page while an external one is in use,
          and until the builder has one. Otherwise its row carries the switch. */}
      {!isLoading && website && (isExternal || !hasHomePage) && (
        <HomePageCard
          websiteId={websiteId}
          landingType={landingType}
          externalHomepageUrl={website.externalHomepageUrl}
        />
      )}

      {isLoading &&
        Array.from({ length: 3 }).map((_, i) => (
          <Box
            key={i}
            className="vtwo:flex vtwo:justify-between vtwo:items-center vtwo:px-6 vtwo:py-5 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white"
          >
            <Skeleton variant="text" width={160} height={24} />
            <Skeleton variant="rounded" width={72} height={30} />
          </Box>
        ))}

      {!isLoading &&
        STANDARD_PAGES.map((standardPage) => {
          const existingPage = pageByHref[standardPage.href];
          const isHomePage = standardPage.href === HOME_PAGE.href;

          // The card above covers the home page when it is missing, and while an
          // external home page replaces it.
          if (isHomePage && (isExternal || !existingPage)) return null;

          if (!existingPage) {
            return <AddPageRow key={standardPage.href} websiteId={websiteId} page={standardPage} />;
          }

          // Only the home page row can swap the website between the builder and an external
          // landing page.
          const landingSwitch =
            isHomePage && website
              ? { landingType, externalHomepageUrl: website.externalHomepageUrl }
              : null;

          return (
            <PageRow
              key={existingPage.id}
              page={existingPage}
              websiteId={websiteId}
              landingSwitch={landingSwitch}
            />
          );
        })}

      {/* An external home page replaces the in-app site, so only the standard
          pages stay manageable. */}
      {!isLoading &&
        !isExternal &&
        customPages.map((page) => <PageRow key={page.id} page={page} websiteId={websiteId} />)}
    </Box>
  );
}

function PageRow({
  page,
  websiteId,
  landingSwitch,
}: {
  page: Page;
  websiteId: string;
  landingSwitch?: PageLandingSwitch | null;
}): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';

  const goToPage = (segment: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('websiteId', websiteId);
    navigate(
      { pathname: `/plus-websites/pages/${page.id}${segment}`, search: params.toString() },
      { state: { pageName: page.name } }
    );
  };

  return (
    <Box className="vtwo:flex vtwo:justify-between vtwo:items-center vtwo:px-6 vtwo:py-5 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white">
      <Typography className="vtwo:font-bold">{page.name}</Typography>
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-3">
        <Button
          variant="contained"
          disableElevation
          onClick={() => goToPage('')}
          sx={{
            borderRadius: '8px',
            backgroundColor: '#6448F1',
            color: '#fff',
            '&:hover': { backgroundColor: '#5338E1' },
          }}
        >
          Edit content
        </Button>
        <PageActionsMenu
          pageName={page.name}
          pageId={page.id}
          websiteId={websiteId}
          organisationId={organisationId}
          onSeoSettings={() => goToPage('/seo-settings')}
          landingSwitch={landingSwitch}
        />
      </Box>
    </Box>
  );
}

export default ContentTab;
